import http from 'node:http';

export interface LoopbackLogin {
  email: string;
  token: string;
  /**
   * The issuing stamp's admin URL, from the callback's `admin_url` param
   * (One Console P5). Absent on redirects from older stamps — the caller
   * then falls back to a pre-resolved admin URL.
   */
  adminUrl?: string;
}

export interface LoopbackServer {
  port: number;
  redirectUri: string;
  /** Resolves with the credentials from the real redirect; rejects on timeout. */
  result: Promise<LoopbackLogin>;
  close(): void;
}

function page(title: string, body: string): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8"><title>' +
    title +
    '</title></head><body style="font-family:system-ui,sans-serif;text-align:center;padding-top:80px;background:#0f1117;color:#e6e6ef">' +
    body +
    '</body></html>'
  );
}

/**
 * One-shot loopback receiver for the CLI login flow.
 *
 * Phase-1 lesson baked in: browsers fire favicon requests and port scanners
 * send stray probes at the loopback port. Anything that is not
 * GET /callback WITH a `token` query param is answered without consuming
 * the listener — the server keeps serving until the real redirect
 * (token + matching state) arrives or the timeout fires.
 */
export function startLoopbackServer(options: {
  expectedState: string;
  timeoutMs: number;
  productName: string;
}): Promise<LoopbackServer> {
  const { expectedState, timeoutMs, productName } = options;
  const server = http.createServer();

  return new Promise((resolveServer, rejectServer) => {
    server.once('error', rejectServer);
    server.listen(0, '127.0.0.1', () => {
      server.off('error', rejectServer);
      const address = server.address();
      if (!address || typeof address === 'string') {
        server.close();
        rejectServer(new Error('Failed to start the local sign-in listener.'));
        return;
      }
      const port = address.port;

      const result = new Promise<LoopbackLogin>((resolve, reject) => {
        const timeout = setTimeout(() => {
          server.close();
          reject(new Error('Sign-in timed out after 10 minutes. Run `ghosty login` again.'));
        }, timeoutMs);
        // Don't let the timer keep a wedged process alive past close().
        timeout.unref?.();

        server.on('request', (req, res) => {
          const url = new URL(req.url ?? '/', `http://127.0.0.1:${port}`);

          // Favicons, health probes, anything not the callback: 404 and keep
          // listening.
          if (url.pathname !== '/callback') {
            res.writeHead(404).end();
            return;
          }

          const token = url.searchParams.get('token');
          const email = url.searchParams.get('email');
          const state = url.searchParams.get('state');

          // No token → not the real redirect (stray probe / favicon-ish
          // request to /callback). Ignore without consuming the listener.
          if (!token) {
            res.writeHead(404).end();
            return;
          }

          if (state !== expectedState || !email) {
            // Possible CSRF or a stale redirect: reject this request but
            // keep waiting for the genuine one.
            res.writeHead(400, { 'Content-Type': 'text/html' });
            res.end(
              page(
                'Sign-in failed',
                '<h1>Sign-in failed</h1><p>This sign-in link is invalid or expired. Return to your terminal and run the login command again.</p>',
              ),
            );
            return;
          }

          res.writeHead(200, { 'Content-Type': 'text/html' });
          res.end(
            page(
              `Signed in — ${productName}`,
              `<h1>You're signed in to ${productName}</h1><p>Signed in as <b>${email}</b>. You can close this tab and return to your terminal.</p>`,
            ),
          );
          clearTimeout(timeout);
          // Give the response a beat to flush, then shut down.
          setTimeout(() => server.close(), 100).unref?.();
          const adminUrl = url.searchParams.get('admin_url');
          resolve({ email, token, ...(adminUrl ? { adminUrl } : {}) });
        });
      });

      resolveServer({
        port,
        redirectUri: `http://127.0.0.1:${port}/callback`,
        result,
        close: () => server.close(),
      });
    });
  });
}
